import React, { useEffect, useState } from 'react';

import { VALIDATION_REGEX } from '../../../../../shared/components/validation.config';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import { trackEvent } from '../../../../../shared/services/utils/tracking/tracking.service';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { CompanyUiModel } from '../../../../../shared/ui-models/company.ui-model';

export interface EditCompanyModalProps {
  data: {
    company?: CompanyUiModel;
  };
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    cancelHandler: () => void;
    editCompanyHandler: (companyId: string, company: Partial<CompanyUiModel>) => void;
  };
}

export const EditCompanyModal: React.FC<EditCompanyModalProps> = ({
  data,
  config,
  eventHandlers
}) => {
  const [companyName, setCompanyName] = useState<FormElementData<string>>({
    value: '',
    isValid: false,
    changed: false
  });
  const [contactPersonName, setContactPersonName] = useState<FormElementData<string>>({
    value: '',
    isValid: false,
    changed: false
  });
  const [contactPersonEmail, setContactPersonEmail] = useState<FormElementData<string>>({
    value: '',
    isValid: false,
    changed: false
  });
  const [contactPersonPhone, setContactPersonPhone] = useState<FormElementData<string>>({
    value: '',
    isValid: false,
    changed: false
  });
  const isMobileView = useWindowResizer();

  useEffect(() => {
    setCompanyName({ value: data?.company?.name || '', isValid: false, changed: false });
    setContactPersonName({
      value: data?.company?.contactPersonName || '',
      isValid: false,
      changed: false
    });
    setContactPersonEmail({
      value: data?.company?.contactPersonEmail || '',
      isValid: false,
      changed: false
    });
    setContactPersonPhone({
      value: data?.company?.contactPersonPhoneNumber || '',
      isValid: false,
      changed: false
    });
  }, [data.company]);

  const handleCompanySubmit = () => {
    if (!data.company?.id) return;

    const updatePayload: Partial<CompanyUiModel> = {};
    let isValid = true;

    if (companyName.changed) {
      isValid = companyName.isValid && isValid;
      if (companyName.isValid && companyName.value !== data.company?.name) {
        updatePayload.name = companyName.value;
      }
    }

    if (contactPersonName.changed) {
      isValid = contactPersonName.isValid && isValid;
      if (
        contactPersonName.isValid &&
        contactPersonName.value !== data.company?.contactPersonName
      ) {
        updatePayload.contactPersonName = contactPersonName.value;
      }
    }

    if (contactPersonEmail.changed) {
      isValid = contactPersonEmail.isValid && isValid;
      if (
        contactPersonEmail.isValid &&
        contactPersonEmail.value !== data.company?.contactPersonEmail
      ) {
        updatePayload.contactPersonEmail = contactPersonEmail.value;
      }
    }

    if (contactPersonPhone.changed) {
      isValid = contactPersonPhone.isValid && isValid;
      if (
        contactPersonPhone.isValid &&
        contactPersonPhone.value !== data.company?.contactPersonPhoneNumber
      ) {
        updatePayload.contactPersonPhoneNumber = contactPersonPhone.value;
      }
    }

    if (isValid && Object.keys(updatePayload).length > 0) {
      eventHandlers.editCompanyHandler(data.company.id, updatePayload);
    }
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.cancelHandler();
        }
      }}
    >
      <div className="flex flex-col gap-3 px-6 py-4">
        <div className="text-2xl font-gilroy-bold font-semibold text-neutral-900">Edit Company</div>
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">Company name</div>
        <InputText
          data={{
            value: companyName.value,
            errorMessage: 'Please enter a valid company name',
            placeholder: 'Enter company name'
          }}
          config={{
            maxRows: 1,
            autoFocus: true,
            validationRegex: VALIDATION_REGEX.COMPANY_NAME,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setCompanyName({ value, isValid, changed: true });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person name
        </div>
        <InputText
          data={{
            value: contactPersonName.value,
            errorMessage: 'Please enter a valid name',
            placeholder: 'Enter contact person name'
          }}
          config={{
            maxRows: 1,
            validationRegex: VALIDATION_REGEX.COMPANY_NAME,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonName({ value, isValid, changed: true });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person email
        </div>
        <InputText
          data={{
            value: contactPersonEmail.value,
            errorMessage: 'Please enter a valid email',
            placeholder: 'Enter contact person email'
          }}
          config={{
            maxRows: 1,
            type: InputTextType.email,
            validationRegex: VALIDATION_REGEX.EMAIL,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonEmail({ value, isValid, changed: true });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
        <div className="text-xl font-medium font-gilroy-medium text-neutral-500">
          Contact person phone
        </div>
        <InputText
          data={{
            value: contactPersonPhone.value,
            errorMessage: 'Please enter a phone number',
            placeholder: 'Enter contact person phone'
          }}
          config={{
            maxRows: 1,
            showErrorMode: InputTextErrorMode.instant,
            required: true
          }}
          eventHandlers={{
            onChangeHandler: (value: string, isValid: boolean) => {
              setContactPersonPhone({ value, isValid, changed: true });
            },
            onEnterPressedHandler: handleCompanySubmit
          }}
        />
      </div>
      <div className="flex flex-col gap-3 px-6 pb-6 sm:flex-row sm:justify-end">
        <Button
          onClick={() => {
            eventHandlers.cancelHandler();
            trackEvent('cancel', {
              category: 'company',
              label: 'edit-company-modal'
            });
          }}
          tracking={{
            action: 'cancel',
            category: 'company',
            label: 'edit-company-modal'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
          type={ButtonType.primaryInverted}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCompanySubmit}
          tracking={{
            action: 'submit',
            category: 'company',
            label: 'edit-company-modal'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};
